import {
  ELEMENT_TABLE,
  ELEMENT_TH,
  ELEMENT_TR,
  type PlateEditor,
  type TDescendant,
  type TElement,
} from "@udecode/plate";
import { type BaseEditor, Node, type NodeEntry, Path, Range } from "slate";
import { v4 as uuidv4 } from "uuid";

import { ELEMENT_RULE } from "../elements";
import { getParentTable } from "./transforms";

const isTableNode = (node: TDescendant | undefined): node is TElement => node?.type === ELEMENT_TABLE;
const isTableRow = (node: TDescendant | undefined): node is TElement => node?.type === ELEMENT_TR;
const isSectionNode = (node: TDescendant | undefined): node is TElement => node?.type === "section";
const getChildren = (node: TDescendant | TElement | undefined) => (Array.isArray(node?.children) ? node.children : []);
const getFirstCellType = (row: TDescendant) => getChildren(row)[0]?.type;
const getFirstCellChildType = (row: TDescendant) => getChildren(getChildren(row)[0])[0]?.type;

/**
 * Plate's clipboard fragment can preserve the table rows/cells while dropping
 * custom row flags. Restore the flags from the decision-table shape so pasted
 * tables still serialize with a header row and an otherwise row.
 */
export const normalizeDecisionTableNode = (node: TDescendant) => {
  if (!isTableNode(node)) {
    return node;
  }

  const rows = getChildren(node);
  return {
    ...node,
    children: rows.map((row: TDescendant, rowIndex: number) => {
      if (!isTableRow(row)) {
        return row;
      }

      const isHeader = row.isHeader ?? (rowIndex === 0 && getFirstCellType(row) === ELEMENT_TH);

      if (isHeader) {
        return {
          ...row,
          isHeader: true,
        };
      }

      return {
        ...row,
        isLast: row.isLast ?? (rowIndex === rows.length - 1 && getFirstCellChildType(row) === ELEMENT_RULE),
      };
    }),
  };
};

export const normalizeDecisionTableFragment = (fragment: TDescendant[]) =>
  Array.isArray(fragment) ? fragment.map(normalizeDecisionTableNode) : fragment;

const regenerateNodeIds = (node: TDescendant): TDescendant => {
  const next = "id" in node ? { ...node, id: uuidv4() } : { ...node };
  if ("children" in next && Array.isArray(next.children)) {
    next.children = next.children.map(regenerateNodeIds);
  }
  return next;
};

/**
 * Pasted/duplicated nodes keep the ids from the copy source. Ids are used to
 * re-locate nodes by identity (see getNodeById), so leaving them duplicated
 * means lookups can silently resolve to the original node instead of the
 * pasted one. Give every id-bearing node in the fragment a fresh id.
 */
export const regenerateFragmentIds = (fragment: TDescendant[]) =>
  Array.isArray(fragment) ? fragment.map(regenerateNodeIds) : fragment;

/**
 * Section copy handling may unwrap a section to `[table]`, while other paths can
 * still hand us `[section -> table]`. Treat both as a single candidate table.
 */
const getSingleSelectedTableFragment = (fragment: TDescendant[]) => {
  const normalizedFragment = normalizeDecisionTableFragment(fragment);
  if (!Array.isArray(normalizedFragment) || normalizedFragment.length !== 1) {
    return null;
  }

  const [node] = normalizedFragment;
  if (isTableNode(node)) {
    return node;
  }

  const sectionChildren = isSectionNode(node) ? getChildren(node) : [];
  if (sectionChildren.length === 1 && isTableNode(sectionChildren[0] as TDescendant)) {
    return normalizeDecisionTableNode(sectionChildren[0] as TDescendant);
  }

  return null;
};

const tableFragmentsMatch = (table: TElement, fragmentTable: TDescendant | null) => {
  const tableChildren = getChildren(table);
  const fragmentChildren = getChildren(fragmentTable ?? undefined);
  if (!fragmentTable || !tableChildren.length || !fragmentChildren.length) {
    return false;
  }

  return (
    tableChildren.length === fragmentChildren.length &&
    Node.string(table) === Node.string(fragmentTable as TElement) &&
    tableChildren.every((row: TDescendant, rowIndex: number) => {
      const fragmentRow = fragmentChildren[rowIndex];
      return isTableRow(row) && isTableRow(fragmentRow) && getFirstCellType(row) === getFirstCellType(fragmentRow);
    })
  );
};

/**
 * Slate sees a visual full-table cut as a range inside the first and last text
 * leaves. This detects that case by comparing the selected fragment with the
 * live table, so row-only or partial-table selections still use normal Slate
 * delete behavior.
 */
export const getSelectedFullTable = (editor: PlateEditor, fragment: TDescendant[]) => {
  const { selection } = editor;
  if (!selection || Range.isCollapsed(selection)) return null;

  const [start, end] = Range.edges(selection);
  const startTable = getParentTable(editor as BaseEditor, { at: start });
  const endTable = getParentTable(editor as BaseEditor, { at: end });
  if (!startTable || !endTable) return null;

  const [table, tablePath] = startTable as NodeEntry<TElement>;
  const [, endTablePath] = endTable as NodeEntry<TElement>;
  if (!Path.equals(tablePath, endTablePath)) return null;

  const fragmentTable = getSingleSelectedTableFragment(fragment);
  if (!tableFragmentsMatch(table, fragmentTable)) return null;

  return {
    fragmentTable,
    table,
    tablePath,
  };
};
